import {
    CoverageType,
    formatDate,
    ListProviderChargemasterItemsRequest,
    ListProviderChargemastersRequest,
    PlaceOfServiceType,
    ProviderChargemaster,
    ProviderChargemasterItem,
    ProviderPayer,
    ProviderProvider,
} from '@enter/core';
import { faker } from '@faker-js/faker';
import _ from 'lodash';
import { IChargemasterDetail, IChargemasterListing } from './chargemaster.store';
import { ClaimGenerator } from './claim.generator';
import { waitForNoReason } from './util';
import { generateAmount, oneOf, upToNTimes } from './util.generator';

export class ChargemasterGenerator {
    public chargemasters: ProviderChargemaster[] = [];
    public chargemasterItems: ProviderChargemasterItem[] = [];
    public payers: ProviderPayer[];
    public providers: ProviderProvider[];

    // TODO: extract index/storeTo/loadFrom as utility class
    public index: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

    constructor(claimGenerator: ClaimGenerator) {
        this.generateEntities(5);
        this.payers = claimGenerator.payers;
        this.providers = claimGenerator.providers;
    }

    public async generateChargemasterListing(
        lpcr: ListProviderChargemastersRequest,
    ): Promise<IChargemasterListing | undefined> {
        const chargemasters = this.chargemasters;
        await waitForNoReason(400);
        return {
            chargemasters,
            limit: lpcr.limit || 20,
            offset: lpcr.offset || 0,
            total: chargemasters.length,
        };
    }
    public async generateChargemaster(chargemasterId: string): Promise<ProviderChargemaster | undefined> {
        let chargemaster = _.find(this.chargemasters, { id: chargemasterId });
        if (!chargemaster) {
            chargemaster = this.generateProviderChargemaster(chargemasterId);
        }
        return chargemaster;
    }
    public async generateChargemasterDetail(
        lpcir: ListProviderChargemasterItemsRequest,
    ): Promise<IChargemasterDetail | undefined> {
        let chargemasterItems = this.loadFromIndex<ProviderChargemasterItem>('chargemasterItems', lpcir.id);
        const total = _.size(chargemasterItems);
        const limit = lpcir.limit;
        const offset = lpcir.offset || 0;
        if (!_.isNil(limit) && !_.isNil(offset)) {
            chargemasterItems = _.slice(chargemasterItems, offset, limit + offset);
        }
        return {
            chargemasterItems,
            limit,
            total,
            offset,
        };
    }

    private generateEntities(count: number = 3): void {
        // generate chargemasters
        _.times(count, n => this.generateProviderChargemaster(faker.string.uuid()));
    }

    private generateProviderChargemaster(id: string = faker.string.uuid()): ProviderChargemaster {
        const created = formatDate('Date')(faker.date.past());
        const chargemaster = {
            id,
            created,
            name: `Test chargemaster (${created})`,
            description: faker.lorem.sentence(),
        };
        this.chargemasters.push(chargemaster);

        const items = upToNTimes(() => this.generateProviderChargemasterItem(), 60);
        this.storeToIndex('chargemasterItems', chargemaster.id, items);
        return chargemaster;
    }

    private generateProviderChargemasterItem(): ProviderChargemasterItem {
        const modifiers = ['A', 'B', 'C', 'D', 'R', 'S', 'T', 'W'];
        const item = {
            id: faker.string.uuid(),
            created: formatDate('Date')(faker.date.past()),
            procedure: String(_.random(30000, 99999)),
            modifiers: _.compact(upToNTimes(() => _.sample(modifiers), 3)),
            amount: generateAmount(),
            allowed: generateAmount(),
            expected: generateAmount(),
            patient: generateAmount(),
            authorization: _.sample([true, false]),
            paperwork: _.sample([true, false]),
            description: faker.lorem.sentence(),
            placeOfServiceType: oneOf(PlaceOfServiceType),
            payer: _.sample(this.payers),
            coverage: oneOf(CoverageType),
            provider: _.sample(this.providers),
        };
        this.chargemasterItems.push(item);
        return item;
    }

    private storeToIndex<T>(model: string, key: string, values: T | T[]): void {
        if (!this.index.has(model)) {
            this.index.set(model, new Map<string, string[]>());
        }
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const items = _.concat([], values);
            const oldIds = index.get(key) || [];
            const newIds = _.map(items, 'id');
            index.set(key, [...oldIds, ...newIds]);
            items.forEach(item => array.indexOf(item) === -1 && array.push(item));
        }
    }

    private loadFromIndex<T>(model: string, key: string): T[] | undefined {
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const ids = index.get(key);
            return _.filter(array, (item: T): boolean => _.includes(ids, _.get(item, 'id')));
        }
    }
}
